import {
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'

import React, {useEffect, useMemo, useState} from 'react'

import {nalogIzvjestaj} from '../../../../api/izvjestaj'

type ParsedNalog = {
    brojNaloga: string
    stavka: string | null
    paket: string
    ponuda: string
}

const normalizeStavka = (value: any): string | null => {
    if (value === undefined || value === null) {
        return null
    }

    const cleanValue = String(value).trim()

    if (
        cleanValue === '' ||
        cleanValue.toLowerCase() === 'null' ||
        cleanValue.toLowerCase() === 'undefined'
    ) {
        return null
    }

    return cleanValue
}

const parseNalog = (value: string): ParsedNalog => {
    const rawValue = value ?? ''

    const pipeParts = rawValue.split('|')

    const brojNaloga = pipeParts[0]?.trim() ?? ''

    const rightSide = pipeParts[1]?.trim() ?? ''

    const rightParts = rightSide.split('-')

    const stavka = normalizeStavka(rightParts[0])
    const paket = rightParts[1]?.trim() ?? ''
    const ponuda = rightParts[2]?.trim() ?? ''

    return {
        brojNaloga,
        stavka,
        paket,
        ponuda,
    }
}

const getNalogKey = (brojNaloga: string, stavka: string | null) => {
    return `${brojNaloga}|${stavka ?? 'null'}`
}

const UtovarKamionaTable = ({
                                data,
                                kamion,
                                robaDetalji,
                                setRobaDetalji,
                            }: any) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const [globalFilter, setGlobalFilter] = useState('')
    const [naloziData, setNaloziData] = useState<any>({})

    useEffect(() => {
        if (!data?.items?.length) {
            setNaloziData({})
            return
        }

        const loadData = async () => {
            try {
                const uniqueMap: any = {}

                data.items.forEach((item: any) => {
                    const {
                        brojNaloga,
                        stavka,
                    } = parseNalog(item.id)

                    if (!brojNaloga) {
                        return
                    }

                    const key = getNalogKey(brojNaloga, stavka)

                    uniqueMap[key] = {
                        brojNaloga,
                        stavka,
                    }
                })

                const uniqueItems: any = Object.values(uniqueMap)

                const responses = await Promise.all(
                    uniqueItems.map(async ({brojNaloga, stavka}: any) => {
                        try {
                            const response = await nalogIzvjestaj({
                                brojNaloga,
                                stavka,
                            })

                            const apiData: any =
                                (response as any)?.data?.data?.proizvodnja ??
                                {}

                            return {
                                key: getNalogKey(brojNaloga, stavka),
                                data: apiData,
                            }
                        } catch (e) {
                            console.error(e)

                            return {
                                key: getNalogKey(brojNaloga, stavka),
                                data: {},
                            }
                        }
                    })
                )

                const mapped: any = {}

                responses.forEach((item: any) => {
                    mapped[item.key] = item.data
                })

                setNaloziData(mapped)

                setRobaDetalji((prev: any) => ({
                    ...prev,
                    ...mapped,
                }))
            } catch (e) {
                console.error(e)
            }
        }

        loadData()
    }, [data])

    const columns = useMemo(() => [
        {
            id: 'Nalog',
            header: 'Broj Naloga',

            cell: ({row}: any) => {
                const {
                    brojNaloga,
                } = parseNalog(row.original.id)

                return brojNaloga || '-'
            },
        },

        {
            id: 'Stavka',
            header: 'Stavka',

            cell: ({row}: any) => {
                const {
                    stavka,
                } = parseNalog(row.original.id)

                return stavka ?? '-'
            },
        },

        {
            id: 'Ponuda',
            header: 'Broj Ponude',

            cell: ({row}: any) => {
                const {
                    ponuda,
                } = parseNalog(row.original.id)

                return ponuda || '-'
            },
        },

        {
            id: 'Paket',
            header: 'Paket#',

            cell: ({row}: any) => {
                const {
                    paket,
                } = parseNalog(row.original.id)

                return paket || '-'
            },
        },

        {
            id: 'Sifra',
            header: 'Šifra',

            cell: ({row}: any) => {
                const {
                    brojNaloga,
                    stavka,
                } = parseNalog(row.original.id)

                const item =
                    naloziData[
                        getNalogKey(brojNaloga, stavka)
                        ]

                return item?.artikal ?? '-'
            },
        },

        {
            id: 'Boja',
            header: 'Boja',

            cell: ({row}: any) => {
                const {
                    brojNaloga,
                    stavka,
                } = parseNalog(row.original.id)

                const item =
                    naloziData[
                        getNalogKey(brojNaloga, stavka)
                        ]

                return (
                    <div>
                        <div>
                             {item?.boja_gore?.BojaNaziv ?? '-'} / {item?.boja_dole?.BojaNaziv ?? '-'}
                        </div>

                    </div>
                )
            },
        },

        {
            id: 'Dimenzije',
            header: 'Dimenzije Paketa',

            accessorFn: (row: any) =>
                `${row.w} X ${row.h} X ${row.d}`,
        },

        {
            id: 'Utovar',
            header: 'Utovar',

            cell: ({row}: any) => (
                <img
                    src={row.original.image_separated}
                    alt=''
                    style={{
                        maxWidth: '120px',
                        maxHeight: '120px',
                        objectFit: 'contain',
                    }}
                />
            ),
        },

        {
            id: 'Korak',
            header: 'Korak po korak',

            cell: ({row}: any) => (
                <img
                    src={row.original.image_sbs}
                    alt=''
                    style={{
                        maxWidth: '120px',
                        maxHeight: '120px',
                        objectFit: 'contain',
                    }}
                />
            ),
        },
    ], [naloziData])

    const table = useReactTable({
        data: data?.items ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),

        state: {
            sorting,
            globalFilter,
        },

        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
    })

    return (
        <div className='col py-10'>
            <div className='col mb-6 h1'>
                Kamion: {kamion}
            </div>

            <div className='col-3 p-5'>
                <img
                    src={data?.image_complete}
                    alt=''
                    className='img-fluid'
                    style={{
                        maxWidth: '400px',
                    }}
                />
            </div>

            <div className='table-responsive mt-3'>
                <table className='table align-middle table-bordered gy-5 no-footer'>
                    <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                            key={headerGroup.id}
                            className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'
                        >
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    className='text-white'
                                    style={{cursor: 'pointer'}}
                                    onClick={header.column.getToggleSortingHandler()}
                                >
                                    {header.isPlaceholder ? null : (
                                        <span>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}

                                            {header.column.getIsSorted() === 'asc' && ' 🔼'}
                                            {header.column.getIsSorted() === 'desc' && ' 🔽'}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>

                    <tbody className='text-gray-600'>
                    {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={9}>
                                <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                    No matching records found
                                </div>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default UtovarKamionaTable